import type { SeoProps } from '@/components/SEO/Seo';

export interface PageMeta
  extends Pick<
    SeoProps,
    'title' | 'description' | 'canonicalPath' | 'image' | 'imageAlt' | 'type' | 'keywords' | 'imageWidth' | 'imageHeight' | 'imageType'
  > {}

const defaultSocialPreview = {
  imageWidth: 1200,
  imageHeight: 630,
} as const;

export const pageMetadata: Record<string, PageMeta> = {
  home: {
    ...defaultSocialPreview,
    title: 'Turbo Labs | AI-first product partner breaking the SaaS iron triangle',
    description:
      'Turbo Labs pairs senior product + engineering pods with agentic automation rails to ship production-grade SaaS in days—speed, scope, and quality without trade-offs.',
    canonicalPath: '/',
    image: '/assets/COVER.jpg',
    imageAlt: 'Turbo Labs cover showing AI-first product, automation, and agentic systems.',
    type: 'website',
    keywords: ['Turbo Labs', 'AI product partner', 'agentic automation', 'Rapid SaaS'],
  },
  about: {
    ...defaultSocialPreview,
    title: 'Inside Turbo Labs: culture engineered for velocity',
    description:
      "Based in Fayetteville, Arkansas, Turbo Labs is the region's premier AI development shop—blending agency, ventures, and institute so teams iterate faster and compound outcomes.",
    canonicalPath: '/about',
    image: '/hero-images/hero-2.jpg',
    imageAlt: 'Turbo Labs culture and studio environment.',
    type: 'profile',
    keywords: [
      'Turbo Labs culture',
      'infinite learners',
      'AI operating system',
      'Fayetteville Arkansas AI development',
      'Ozarks software studio',
    ],
  },
  services: {
    ...defaultSocialPreview,
    title: 'Rapid SaaS pods & agentic delivery playbooks',
    description:
      'See how Rapid SaaS engagements unlock launch-critical software with agent-assisted workflows, daily evidence, and pods built to meet board-level deadlines.',
    canonicalPath: '/services',
    image: '/hero-images/hero-3.jpg',
    imageAlt: 'Rapid SaaS delivery artifacts and interface mockups.',
    type: 'product',
    keywords: ['Rapid SaaS', 'AI delivery pods', 'agent-assisted engineering'],
  },
  work: {
    ...defaultSocialPreview,
    title: 'Case studies: shipping AI platforms & automation',
    description:
      'Browse delivery indexes showing how Turbo Labs shipped enterprise AI platforms, agent ecosystems, and automation rails in record time.',
    canonicalPath: '/work',
    image: '/case-studies/case-study.png',
    imageAlt: 'Collection of Turbo Labs case study thumbnails.',
    type: 'website',
    keywords: ['AI case studies', 'automation portfolio', 'Turbo Labs work'],
  },
  'work-gravitate-energy': {
    ...defaultSocialPreview,
    title: 'Gravitate Energy delivery index preview',
    description:
      'Get notified when we publish how Turbo Labs delivered Gravitate Energy’s freight management platform in six weeks with automation-first ops.',
    canonicalPath: '/work/gravitate-energy',
    image: '/case-studies/case-study.png',
    imageAlt: 'Gravitate Energy logistics dashboard preview.',
    type: 'article',
    keywords: ['Gravitate Energy case study', 'freight automation', 'Rapid SaaS delivery'],
  },
  'work-momentum-ai': {
    ...defaultSocialPreview,
    title: 'Momentum AI connected hydration case study',
    description:
      'See what is coming in the Momentum AI build diary covering IoT telemetry, AI analytics, and fulfillment automation for the connected hydration platform.',
    canonicalPath: '/work/momentum-ai',
    image: '/images/blog/ai-team.png',
    imageAlt: 'Momentum AI connected hydration platform illustration.',
    type: 'article',
    keywords: ['Momentum AI', 'IoT analytics', 'automation case study'],
  },
  'work-velvette-night': {
    ...defaultSocialPreview,
    title: 'Velvette Night reflective safety suite teaser',
    description:
      'Preview the forthcoming Velvette Night breakdown detailing the reflective hardware launch, computer vision companion app, and agentic QA workflow.',
    canonicalPath: '/work/velvette-night',
    image: '/images/blog/ai-workforce-featured.png',
    imageAlt: 'Velvette Night reflective safety product visuals.',
    type: 'article',
    keywords: ['Velvette Night', 'computer vision QA', 'product delivery journal'],
  },
  mach: {
    ...defaultSocialPreview,
    title: 'Mach: intelligence multiplier for tech companies',
    description:
      'Mach connects human knowledge with machine capability—custom LLMs, autonomous agents, and evolving automation that scale engineering outcomes.',
    canonicalPath: '/services/mach',
    image: '/hero-images/hero-4.jpg',
    imageAlt: 'Mach program visuals representing intelligence multiplier.',
    type: 'product',
    keywords: ['Mach program', 'custom LLMs', 'autonomous agents'],
  },
  nexus: {
    ...defaultSocialPreview,
    title: 'Nexus: agent ecosystems for manual industries',
    description:
      'Nexus delivers production-grade agent ecosystems with reasoning chains, RAG pipelines, and operational guardrails built from daily deployments.',
    canonicalPath: '/services/nexus',
    image: '/images/blog/ai-team.png',
    imageAlt: 'Nexus agent ecosystem workflow illustration.',
    type: 'product',
    keywords: ['Nexus agents', 'agent ecosystem', 'production RAG'],
  },
  replicant: {
    ...defaultSocialPreview,
    title: 'Replicant: AI-powered development service',
    description:
      'Replicant gives teams a dedicated AI development partner to replicate winning experiences, automate delivery, and keep product velocity high.',
    canonicalPath: '/key-services/replicant',
    image: '/images/blog/ai-workforce-featured.png',
    imageAlt: 'Replicant AI development workflows in action.',
    type: 'product',
    keywords: ['Replicant service', 'AI development partner', 'automation pods'],
  },
  blog: {
    ...defaultSocialPreview,
    title: 'Turbo Labs blog: AI strategy, delivery & culture',
    description:
      'Read essays and delivery notes on AI strategy, automation frameworks, talent development, and the operating system behind Turbo Labs.',
    canonicalPath: '/blog',
    image: '/images/blog/ai-workforce-featured.png',
    imageAlt: 'Turbo Labs blog feature collage.',
    type: 'website',
    keywords: ['AI insights', 'automation blog', 'Turbo Labs articles'],
  },
};

export const getPageMeta = (key: keyof typeof pageMetadata): PageMeta => pageMetadata[key];
